<template>
  <div data-tourid="tasks-creator-drawer">
    <v-slide-y-reverse-transition
      origin="right center 0"
      @after-leave="afterLeaveAnimation"
    >
      <v-card
        outlined
        right
        fixed
        class="creation-task-drawer pa-0 rounded-top"
        width="520"
        hide-overlay
        v-show="visible"
        :class="{
          sizing: view === DRAWER_VIEW.FULL,
          minimized: view === DRAWER_VIEW.MINIMIZED,
        }"
        :loading="isLoading"
      >
        <div class="sizing scrollable">
          <v-card-text v-if="view === DRAWER_VIEW.FULL" class="pa-0">
            <TaskDetail
              :task="task"
              :attachments="attachments"
              @changeTask="handleTaskUpdate"
              @updateAttachments="updateAttachments"
              @close="handleClose"
              @minimize="handleMinimize"
            />
          </v-card-text>
          <v-divider />
          <v-card-actions
            v-if="view === DRAWER_VIEW.FULL"
            class="px-6 d-flex flex-column justify-end align-end save-container"
          >
            <Button
              class="mt-2 mb-2"
              :loading="isLoading"
              :disabled="hasError || isLoading"
              :text="$t('tasks.create_task')"
              @click="handleCreateTask"
            />
          </v-card-actions>
          <v-card-text
            v-if="view === DRAWER_VIEW.MINIMIZED"
            class="d-flex justify-space-between align-center cursor-pointer"
            @click="handleExpand"
          >
            <span
              class="body2 blue-grey--text text--darken-3 text-truncate mr-6"
              style="max-width: 85%;"
            >
              <v-icon size="16" color="blue-grey darken-1" class="mr-2">
                mdi-check
              </v-icon>
              {{ minimizedTaskName }}
            </span>
            <div>
              <v-icon
                color="blue-grey darken-1"
                size="20"
                class="mr-2"
                @click="view = DRAWER_VIEW.FULL"
              >
                mdi-arrow-expand
              </v-icon>
              <v-icon
                :ripple="false"
                size="20"
                color="blue-grey darken-1"
                class="cursor-pointer"
                @click="deleteTask"
              >
                mdi-close
              </v-icon>
            </div>
          </v-card-text>
        </div>
      </v-card>
    </v-slide-y-reverse-transition>
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :title="$t('tasks.delete_task_title')"
      :text="$t('tasks.delete_task_message')"
      :confirmText="$t('common.button.close')"
      @cancel="showDeleteModal = false"
      @delete="deleteTask"
    />
    <ConfirmationModal
      confirmColor="danger"
      :maxWidth="454"
      :title="$t('tasks.unsaved_task')"
      :text="$t('tasks.unsaved_task_description')"
      :confirmText="$t('common.button.delete')"
      :cancelText="$t('common.button.cancel')"
      :show="showUnsavedModal"
      @cancel="showUnsavedModal = false"
      @confirm="
        showUnsavedModal = false
        setDefaultTaskInfo()
        openDrawer()
      "
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import _ from 'lodash'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import getEntityId from '@/lib/getEntityId'

import TaskDetail from './TaskDetail.vue'
import Button from '@/components/UI/Button.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import ConfirmationModal from '@/components/Modals/ConfirmationModal.vue'

const DRAWER_VIEW = {
  FULL: 'full',
  MINIMIZED: 'minimized',
}

export default {
  name: 'TaskCreationDrawer',

  mixins: [mixin, trackerMixin],

  components: {
    TaskDetail,
    Button,
    DeleteConfirmationModal,
    ConfirmationModal,
  },

  props: {},

  data() {
    return {
      visible: false,
      DRAWER_VIEW,
      taskToCreate: {},
      task: {
        name: '',
        dueDate: null,
        status: this.$constants.tasksStatus.open,
        description: null,
        priority: null,
        assignees: [],
        media: [],
      },
      attachments: [],
      isLoading: false,
      view: DRAWER_VIEW.FULL,
      showDeleteModal: false,
      showUnsavedModal: false,
      hasChanges: false,
      entityFrom: '',
    }
  },

  async created() {
    eventHub.$on(HUB_EVENTS.TASK_CREATION_DRAWER.CREATE, this.handleOpenDrawer)
  },

  computed: {
    ...mapGetters(['organizationId', 'account']),

    assignee() {
      return this.task?.assignees[0]
    },

    hasError() {
      return !this.task?.name?.trim()
    },

    minimizedTaskName() {
      return this.task?.name || this.$t('tasks.new_task')
    },

    hasAttachments() {
      return !_.isEmpty(this.attachments)
    },

    attachmentsIds() {
      return this.attachments?.map?.(file => file.id) ?? []
    },

    taskMediaIds() {
      return this.task?.media?.map?.(media => media.id) ?? null
    },
  },

  methods: {
    async handleCreateTask() {
      try {
        if (this.hasError) return
        this.isLoading = true
        // Tags no longer loaded from assignees or creator
        const tags = this.task?.tags?.map?.(getEntityId) ?? []

        const { data } = await api.tasks.create(
          this.organizationId,
          _.omitBy(
            {
              ...this.task,
              assignees: this.assignee?.id ? [this.assignee?.id] : [],
              site: this.assignee?.site?._id ?? this.account?.site?._id,
              tags: tags,
              media: this.taskMediaIds,
            },
            _.isNull
          )
        )

        if (this.hasAttachments) {
          this.attachmentsIds?.forEach(id => {
            api.tasks.attach(this.organizationId, data.id, { media: id })
          })
        }
        eventHub.$emit(HUB_EVENTS.TASK_CREATION_DRAWER.CREATED, this.task)
        this.setDefaultTaskInfo()
        this.visible = false
        this.hasChanges = false
        this.trackTasks('tasks.create', {
          from: this.entityFrom
            ? `${this.entityFrom}_createDrawer`
            : 'createDrawer',
          nameLength: this.task.name?.length,
          status: this.task.status,
          priority: this.task.priority,
          hasDescription: Boolean(this.task.description),
          hasMedia: !_.isEmpty(this.taskMediaIds),
          hasDueDate: Boolean(this.task.dueDate),
          hasAssignee: Boolean(this.assignee?.id),
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleTaskUpdate(taskProperty, value) {
      this.task = {
        ...this.task,
        [taskProperty]: value,
      }
      this.hasChanges = true
    },

    setDefaultTaskInfo() {
      this.task = _.clone({
        name: '',
        dueDate: null,
        status: this.$constants.tasksStatus.open,
        description: '',
        priority: null,
        assignees: [],
        media: [],
      })
      this.attachments = []
    },

    handleClose() {
      if (this.hasChanges) return (this.showDeleteModal = true)
      this.trackTasks('tasks.close', {
        from: 'create',
      })
      this.setDefaultTaskInfo()
      return (this.visible = false)
    },

    discardTask() {
      this.showUnsavedModal = false
      this.setDefaultTaskInfo()
      this.openDrawer()
      this.trackTasks('tasks.discardTask', { option: 'create' })
    },

    deleteTask() {
      this.showDeleteModal = false
      this.visible = false
      this.hasChanges = false
      this.setDefaultTaskInfo()
    },

    afterLeaveAnimation() {
      this.view = DRAWER_VIEW.FULL
    },

    handleOpenDrawer(data, from) {
      this.entityFrom = from ?? ''
      this.taskToCreate = data
      if (this.hasChanges) this.showUnsavedModal = true
      else this.openDrawer()
    },

    openDrawer() {
      this.task = { ...this.task, ...this.taskToCreate }
      this.visible = true
      this.view = DRAWER_VIEW.FULL
    },

    updateAttachments(attachment) {
      this.hasChanges = true
      this.attachments = [...this.attachments, attachment]
    },

    handleMinimize() {
      this.view = DRAWER_VIEW.MINIMIZED
      this.trackTasks('tasks.minimize')
    },

    handleExpand() {
      this.view = DRAWER_VIEW.FULL
      this.trackTasks('tasks.maximize')
    },
  },

  beforeDestroy() {
    eventHub.$off(HUB_EVENTS.TASK_CREATION_DRAWER.CREATE)
  },
}
</script>
<style lang="scss" scoped>
.creation-task-drawer {
  box-shadow: $elevation !important;
  bottom: 0;
  right: 40px;
  position: fixed;
  z-index: 8;
}

.scrollable {
  overflow: auto;
  padding-bottom: 55px !important;
}

.sizing {
  height: 580px;
}

.minimized {
  height: 55px;
}
.save-container {
  position: absolute;
  width: 100%;
  bottom: 0;
  background-color: $color-white;
  border-top: 1px solid $color-blue-grey-lighten2;
}
</style>
